// mintHandoff.ts — the beat that carries the MINT cube out of the assembled mark
// and into the Latently room, landing exactly on the MintNest glyph above "Meet
// Latently." Desktop only (>= 900px, motion allowed) — mounted by Landing.tsx next
// to initMarkChoreography.
//
// The flyer is a synthetic element (Landing.tsx renders it as the last child of
// .landing: <div className="mint-flyer" data-mark="mint-flyer"><CubeGlyph tone="mint"
// size={48} /></div>, absolutely positioned, opacity 0 by default in Landing.css) —
// this module never creates DOM, it only measures two real elements and moves a third.
//
//   START — the assembly SVG's mint group ([data-cube="mint"] inside .assembly__mark).
//   Assembly.tsx's own scroll effect leaves that group's transform at its assembled
//   value for every progress p >= 0.32 (mint's own fly-in window ends there), and that
//   is ALSO the JSX default rendered before Assembly's effect ever touches it (its own
//   file comment: "the JSX default IS the finished, assembled pose"). So its rect never
//   needs re-deriving from a formula — but unlike END below, START is NOT static in
//   document space for this module's whole flight: the flight now starts at p = 0.96,
//   inside the mark's HOLD (p in [0.92, 1], a beat after the last edge draws), while .assembly__stage may still be
//   pinned (sticky top:0). A pinned element's VIEWPORT rect stays constant while it's
//   stuck, so its DOCUMENT position (rect.top + scrollY) keeps climbing in lockstep
//   with scrollY for as long as the pin holds — it is not safe to cache. So START is
//   read LIVE, every tick, via liveStartBox(): getBoundingClientRect() on the mint
//   group's own faces + window.scrollY, same "drawn cube" union technique as always.
//   Once scrollY passes the point where the stage unpins, that same live read settles
//   into a constant automatically (the falling rect.top and the rising scrollY cancel
//   out once the element is in normal flow) — one formula, no branch needed for the
//   pinned vs. settled portions of the flight.
//
//   END — the MintNest glyph ([data-mark="mint-nest"]). It sits in plain, unpinned
//   document flow, so its own getBoundingClientRect + scrollY would be its document
//   box at any time EXCEPT one: markChoreography.ts gives this same element its own
//   separate one-shot "settle" entrance (a pre-entrance translateY:14/scale:0.92 held
//   until the Latently section is itself 60% into view, which happens LATER than this
//   module's own hEnd) — so measuring it naively while that entrance hasn't played yet
//   would land the flyer on the pre-entrance box, not the rest pose it settles into.
//   measureEndBox works around this without touching markChoreography.ts (out of this
//   module's ownership) by zeroing out style.transform for the single synchronous
//   read and restoring whatever was there straight after — no repaint happens in
//   between, so this never flashes. END stays genuinely static in document space
//   (MintNest never sits inside a pinned ancestor), so it is measured once and cached.
//
// START and END are read as the DRAWN CUBE's own box, not the flyer/MintNest SVG's
// outer 48x48 element box, and the flyer's native geometry is measured the same way
// (see measureFlyerGeometry): CubeGlyph's viewBox is not square (a cube is taller
// than it is wide), so its svg's own preserveAspectRatio="xMidYMid meet" default
// letterboxes the drawn cube inside that square, inset and narrower than the square
// itself. Interpolating (and scaling) the OUTER square would put the flyer's actual
// drawn cube pixels off by double digits from the source/target cube's own pixels at
// the swap instant — exactly the "single-pixel jump" the owner watches for. Reading
// the drawn cube's own box on both ends (the mint group's own faces for the source;
// the union of MintNest's three <polygon> faces for the target) and knowing the
// flyer's own cube-vs-outer-box inset lets scale/position be derived so the flyer's
// DRAWN CUBE lands exactly on target, not just its containing square.
//
// hStart is the scrollY at which the assembly track's own scroll progress p reaches
// 0.96 (trackDocTop + 0.96 * (trackHeight - viewportHeight) — trackDocTop and
// trackHeight are static document quantities, mirroring the p formula Assembly.tsx
// itself uses). hEnd is the scrollY at which the MintNest's top reaches 40% of the
// viewport height. Both depend on the CURRENT viewport height, so a resize has to
// redo this arithmetic even though the document quantities they start from don't.
//
// Re-measured: once on init, again on resize and on document.fonts.ready (a webfont
// swap reflows text and can shift either endpoint with no resize event at all), and
// once more, lazily, the first time the handoff range is entered — a cheap insurance
// re-check in case init ran before layout had fully settled. START, additionally, is
// re-read live on every tick of the flight itself (see above) — that one quantity
// can't wait for the next remeasure().
import { prefersReducedMotion } from './reducedMotion'

function clamp01(v: number): number {
  if (v < 0) return 0
  if (v > 1) return 1
  return v
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

// Smoothstep (3h² − 2h³) — symmetric like the cubic it replaces (so the reverse,
// scroll-up trip still reads exactly like the forward one played backwards: h is
// recomputed fresh from scrollY on every tick, there is no stored direction or state
// to get out of sync), but with a gentler shoulder and a higher peak slope (1.5x the
// average, at h = 0.5) — the flight reads faster through its middle and eases into
// both ends more softly than the cubic did.
function smoothstep(x: number): number {
  return x * x * (3 - 2 * x)
}

type Box = { top: number; left: number; width: number; height: number }

function toDocBox(rect: { top: number; left: number; width: number; height: number }): Box {
  return {
    top: rect.top + window.scrollY,
    left: rect.left + window.scrollX,
    width: rect.width,
    height: rect.height,
  }
}

// The union of several elements' own rects — used to get one CubeGlyph's DRAWN cube
// box from its three face <polygon>s, since the <svg> itself is a square canvas the
// cube is letterboxed inside (see the file header comment on why that outer square
// is the wrong thing to interpolate).
function unionRect(els: Element[]): { top: number; left: number; width: number; height: number } {
  let top = Infinity
  let left = Infinity
  let right = -Infinity
  let bottom = -Infinity
  for (const el of els) {
    const r = el.getBoundingClientRect()
    top = Math.min(top, r.top)
    left = Math.min(left, r.left)
    right = Math.max(right, r.left + r.width)
    bottom = Math.max(bottom, r.top + r.height)
  }
  return { top, left, width: right - left, height: bottom - top }
}

// See the START comment above: this is called fresh every tick of the flight, not
// cached, because the mint group's document position keeps moving with scroll for as
// long as .assembly__stage is still pinned. getBoundingClientRect() + scrollY gives
// the true document box regardless of whether the group's pinned ancestor is
// currently stuck or already settled — no branch needed for the two cases.
function liveStartBox(mintGroup: SVGGElement): Box {
  const faces = Array.from(mintGroup.querySelectorAll('polygon'))
  return toDocBox(unionRect(faces))
}

// See the END comment above: read the box as if markChoreography's pre-entrance
// transform were never applied, restoring it immediately after so nothing flashes.
// The box itself is the union of the three face polygons (the drawn cube), not the
// svg's own 48x48 outer square.
function measureEndBox(mintNest: HTMLElement): Box {
  const priorTransform = mintNest.style.transform
  mintNest.style.transform = 'none'
  const faces = Array.from(mintNest.querySelectorAll('polygon'))
  const box = toDocBox(unionRect(faces))
  mintNest.style.transform = priorTransform
  return box
}

// The flyer's own native (unscaled, untransformed) geometry: where its drawn cube
// sits inset within its own outer element box, and how wide/tall that cube is at
// scale 1. Measured once per remeasure() (the flyer's own markup never changes), so
// every tick's scale/position math can derive the OUTER box position from a target
// CUBE box without re-deriving this inset every frame.
interface FlyerGeometry {
  insetLeft: number
  insetTop: number
  cubeWidth: number
}

function measureFlyerGeometry(flyer: HTMLElement): FlyerGeometry {
  const priorTransform = flyer.style.transform
  const priorOpacity = flyer.style.opacity
  flyer.style.transform = 'none'
  flyer.style.opacity = '0' // stays invisible for this synchronous read; nothing paints
  const outer = flyer.getBoundingClientRect()
  const faces = Array.from(flyer.querySelectorAll('polygon'))
  const cube = unionRect(faces)
  flyer.style.transform = priorTransform
  flyer.style.opacity = priorOpacity
  return {
    insetLeft: cube.left - outer.left,
    insetTop: cube.top - outer.top,
    cubeWidth: cube.width,
  }
}

interface Anchors {
  end: Box
  trackDocTop: number
  trackHeight: number
  mintNestDocTop: number
  flyer: FlyerGeometry
}

function measureAnchors(track: HTMLElement, mintNest: HTMLElement, flyer: HTMLElement): Anchors {
  const end = measureEndBox(mintNest)
  const trackRect = track.getBoundingClientRect()
  const trackDocTop = trackRect.top + window.scrollY
  return {
    end,
    trackDocTop,
    trackHeight: trackRect.height,
    mintNestDocTop: end.top,
    flyer: measureFlyerGeometry(flyer),
  }
}

// hStart: the scrollY at which the assembly track's own scroll progress p (same
// formula Assembly.tsx computes from scroll) reaches 0.96 — partway through the
// mark's HOLD, p in [0.96, 1], while it's still fully assembled and (often) still
// pinned. hEnd: the scrollY at which the MintNest's top reaches 40% of the viewport
// height (the room has arrived). Falls back to a fixed 480px range starting at
// hStart if hEnd computes at or before hStart (a viewport tall enough, or a gap
// short enough, that the two thresholds invert).
function computeRange(anchors: Anchors, viewportHeight: number): { hStart: number; hSpan: number } {
  const hStart = anchors.trackDocTop + 0.96 * (anchors.trackHeight - viewportHeight)
  const hEndRaw = anchors.mintNestDocTop - 0.4 * viewportHeight
  const hSpan = hEndRaw > hStart ? hEndRaw - hStart : 480
  return { hStart, hSpan }
}

export function initMintHandoff(root: HTMLElement): () => void {
  if (prefersReducedMotion()) {
    // No-op: the SVG mint stays in the mark and the MintNest stays visible, both, as
    // today — nothing to clean up because nothing was ever touched.
    return () => {}
  }

  const track = root.querySelector<HTMLElement>('.assembly__track')
  const mintGroup = root.querySelector<SVGGElement>('[data-cube="mint"]')
  const mintNest = root.querySelector<HTMLElement>('[data-mark="mint-nest"]')
  const flyer = root.querySelector<HTMLElement>('[data-mark="mint-flyer"]')
  const latentlySection = root.querySelector<HTMLElement>('[data-mood="latently"]')
  if (!track || !mintGroup || !mintNest || !flyer) return () => {}

  const bpQuery = window.matchMedia('(min-width: 900px)')

  let anchors: Anchors | null = null
  let range: { hStart: number; hSpan: number } | null = null
  let enteredRangeOnce = false
  // Cached alongside anchors rather than re-read via root.getBoundingClientRect() on
  // every mid-flight tick: .landing's own document position doesn't move any more
  // often than the other measured (cached) quantities do.
  let rootDocTop = 0
  let rootDocLeft = 0

  function remeasure(): void {
    anchors = measureAnchors(track!, mintNest!, flyer!)
    range = computeRange(anchors, window.innerHeight)
    const rootRect = root.getBoundingClientRect()
    rootDocTop = rootRect.top + window.scrollY
    rootDocLeft = rootRect.left + window.scrollX
  }

  // Below 900px (or right after crossing down into it): the mobile/tablet layout
  // never ran this module's transforms, so there is nothing to reset back to except
  // clearing whatever inline styles a prior >=900px session left behind.
  function clearInline(): void {
    flyer!.style.opacity = ''
    flyer!.style.transform = ''
    flyer!.style.top = ''
    flyer!.style.left = ''
    mintGroup!.style.opacity = ''
    mintNest!.style.opacity = ''
  }

  function applyHandoff(h: number): void {
    if (h <= 0) {
      flyer!.style.opacity = '0'
      mintGroup!.style.opacity = ''
      mintNest!.style.opacity = ''
      return
    }
    if (h >= 1) {
      flyer!.style.opacity = '0'
      mintGroup!.style.opacity = '0' // the cube has left the mark
      mintNest!.style.opacity = ''
      return
    }
    if (!anchors) return
    const t = smoothstep(h)
    // START is read live every tick (see file header) — END stays the cached,
    // zeroed-transform read from the last remeasure(). Interpolate the DRAWN CUBE's
    // own box (both ends are already read that way), then derive the flyer's OUTER
    // box from its own native cube-vs-outer inset so the flyer's drawn cube — not
    // its letterboxing square — lands exactly on that interpolated box.
    const start = liveStartBox(mintGroup!)
    const cubeLeft = lerp(start.left, anchors.end.left, t)
    const cubeTop = lerp(start.top, anchors.end.top, t)
    const cubeWidth = lerp(start.width, anchors.end.width, t)
    const scale = cubeWidth / anchors.flyer.cubeWidth
    const left = cubeLeft - anchors.flyer.insetLeft * scale
    const top = cubeTop - anchors.flyer.insetTop * scale
    // Gentle arc, not a straight slide: a lift tied to h itself (not time), zero at
    // both ends so it never disturbs the pixel-continuous swap at h = 0+ or h = 1.
    // distance is derived from the SAME live start read used above, not a cached
    // value, so the arc stays proportional even while start is still drifting with
    // the pin during the hold portion of the flight.
    const distance = Math.hypot(anchors.end.left - start.left, anchors.end.top - start.top)
    const lift = -0.12 * distance * Math.sin(Math.PI * h)

    mintGroup!.style.opacity = '0'
    mintNest!.style.opacity = '0'
    flyer!.style.opacity = '1'
    flyer!.style.left = `${(left - rootDocLeft).toFixed(2)}px`
    flyer!.style.top = `${(top - rootDocTop).toFixed(2)}px`
    flyer!.style.transform = `translateY(${lift.toFixed(2)}px) scale(${scale.toFixed(4)})`
  }

  function tick(): void {
    if (!range) return
    let h = clamp01((window.scrollY - range.hStart) / range.hSpan)
    if (h > 0 && !enteredRangeOnce) {
      enteredRangeOnce = true
      remeasure()
      if (range) h = clamp01((window.scrollY - range.hStart) / range.hSpan)
    }
    applyHandoff(h)
  }

  let frameQueued = false
  let frameHandle = 0
  function onScroll(): void {
    if (frameQueued) return
    frameQueued = true
    frameHandle = requestAnimationFrame(() => {
      frameQueued = false
      tick()
    })
  }

  let running = false
  function start(): void {
    if (running || !bpQuery.matches) return
    running = true
    window.addEventListener('scroll', onScroll, { passive: true })
    tick() // land on the current scroll position immediately, don't wait for a scroll
  }
  function stop(): void {
    if (!running) return
    running = false
    window.removeEventListener('scroll', onScroll)
    if (frameQueued) cancelAnimationFrame(frameHandle)
    frameQueued = false
  }

  // IntersectionObserver-gated: the scroll+rAF loop above only runs while the region
  // spanning the two sections is actually on screen. Assembly and MeetLatently are
  // adjacent siblings (nothing renders between them), so the true DOM gap between
  // .assembly__track's bottom and the Latently section's top is at most one section's
  // own top padding (--sec, well under one viewport tall) — the small rootMargin below
  // is cheap insurance against that gap, not a load-bearing requirement.
  const intersecting = new Set<Element>()
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) intersecting.add(entry.target)
        else intersecting.delete(entry.target)
      }
      if (intersecting.size > 0) start()
      else stop()
    },
    { rootMargin: '15% 0px 15% 0px' }
  )
  io.observe(track)
  if (latentlySection) io.observe(latentlySection)

  function onResize(): void {
    if (!bpQuery.matches) return
    remeasure()
    tick()
  }

  function onBreakpointChange(): void {
    if (bpQuery.matches) {
      remeasure()
      if (intersecting.size > 0) start()
      tick()
    } else {
      stop()
      clearInline()
    }
  }

  if (bpQuery.matches) remeasure()

  window.addEventListener('resize', onResize)
  bpQuery.addEventListener('change', onBreakpointChange)

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      if (!bpQuery.matches) return
      remeasure()
      tick()
    })
  }

  return () => {
    stop()
    io.disconnect()
    window.removeEventListener('resize', onResize)
    bpQuery.removeEventListener('change', onBreakpointChange)
  }
}
